import styles from "./AppointmentDetailModal.module.scss";
import Button from "../../../../../../../components/Button/Button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faXmark,
  faClock,
  faCalendarDay,
  faSun,
  faCloudSun,
  faMoon,
  faListCheck,
} from "@fortawesome/free-solid-svg-icons";

const STATUS_LABELS = {
  pending: "En attente",
  confirmed: "Confirmé",
  completed: "Terminé",
  cancelled: "Annulé",
};

const STATUS_COLORS = {
  pending: "#f0ad4e",
  confirmed: "#5bc0de",
  completed: "#28a745",
  cancelled: "#c0392b",
};

function formatTime(timeString) {
  if (!timeString) return null;
  return timeString.slice(0, 5);
}

// Durée en minutes entre deux "HH:MM:SS"
function getDurationMinutes(startTime, endTime) {
  if (!startTime || !endTime) return null;
  const [startH, startM] = startTime.split(":").map(Number);
  const [endH, endM] = endTime.split(":").map(Number);
  return endH * 60 + endM - (startH * 60 + startM);
}

// Classification du moment de la journée à partir de l'heure de début
function getTimeOfDay(startTime) {
  if (!startTime) return null;
  const hour = Number(startTime.split(":")[0]);
  if (hour < 12) return { label: "Matin", icon: faCloudSun };
  if (hour < 18) return { label: "Après-midi", icon: faSun };
  return { label: "Soirée", icon: faMoon };
}

// getDay() : 0 = dimanche, 6 = samedi
function getWeekdayInfo(dateValue) {
  if (!dateValue) return null;
  const date = new Date(dateValue);
  const dayIndex = date.getDay();
  const isWeekend = dayIndex === 0 || dayIndex === 6;
  const weekdayLabel = date.toLocaleDateString("fr-FR", { weekday: "long" });
  return { weekdayLabel, isWeekend };
}

function AppointmentDetailModal({ isOpen, onClose, appointment }) {
  if (!isOpen || !appointment) return null;

  const hasDate = !!appointment.appointment_date;

  const formattedDate = hasDate
    ? new Date(appointment.appointment_date).toLocaleDateString("fr-FR", {
        day: "2-digit",
        month: "long",
        year: "numeric",
      })
    : "Date non planifiée";

  const startTime = formatTime(appointment.start_time);
  const endTime = formatTime(appointment.end_time);
  const durationMinutes = getDurationMinutes(
    appointment.start_time,
    appointment.end_time,
  );
  const timeOfDay = getTimeOfDay(appointment.start_time);
  const weekdayInfo = hasDate
    ? getWeekdayInfo(appointment.appointment_date)
    : null;

  const serviceCount = appointment.services?.length || 0;
  const averagePricePerService =
    serviceCount > 0 && appointment.total_amount != null
      ? Number(appointment.total_amount) / serviceCount
      : null;

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className={styles.closeButton}
          onClick={onClose}
          aria-label="Fermer"
        >
          <FontAwesomeIcon icon={faXmark} />
        </button>

        <div className={styles.modalHeader}>
          <h3 className={styles.modalDate}>{formattedDate}</h3>
          {weekdayInfo && (
            <p className={styles.modalWeekday}>
              {weekdayInfo.weekdayLabel}
              {weekdayInfo.isWeekend && (
                <span className={styles.weekendTag}>Week-end</span>
              )}
            </p>
          )}
          {(startTime || endTime) && (
            <p className={styles.modalTime}>
              <FontAwesomeIcon icon={faClock} /> {startTime}
              {endTime && ` - ${endTime}`}
              {durationMinutes != null && ` (${durationMinutes} min)`}
            </p>
          )}
          <span
            className={styles.statusBadge}
            style={{
              backgroundColor: STATUS_COLORS[appointment.status] || "#999",
            }}
          >
            {STATUS_LABELS[appointment.status] || appointment.status}
          </span>
        </div>

        <div className={styles.modalBody}>
          {/* ---------- Contexte temporel ---------- */}
          <div className={styles.infoGrid}>
            {timeOfDay && (
              <div className={styles.infoChip}>
                <FontAwesomeIcon icon={timeOfDay.icon} />
                <span>{timeOfDay.label}</span>
              </div>
            )}
            {weekdayInfo && (
              <div className={styles.infoChip}>
                <FontAwesomeIcon icon={faCalendarDay} />
                <span>
                  {weekdayInfo.isWeekend ? "Week-end" : "Jour de semaine"}
                </span>
              </div>
            )}
            <div className={styles.infoChip}>
              <FontAwesomeIcon icon={faListCheck} />
              <span>
                {serviceCount} prestation{serviceCount > 1 ? "s" : ""}
              </span>
            </div>
          </div>

          {/* ---------- Détail des prestations ---------- */}
          <h4 className={styles.sectionLabel}>Prestations réalisées</h4>

          {appointment.services && appointment.services.length > 0 ? (
            <table className={styles.servicesTable}>
              <tbody>
                {appointment.services.map((service, index) => (
                  <tr key={`${service.id}-${index}`}>
                    <td>{service.name}</td>
                    <td className={styles.priceCell}>
                      {service.unitPrice.toFixed(2)} €
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className={styles.emptyServices}>
              Aucune prestation renseignée.
            </p>
          )}

          {/* ---------- Récapitulatif financier ---------- */}
          <div className={styles.summaryBlock}>
            {averagePricePerService != null && (
              <div className={styles.summaryLine}>
                <span>Prix moyen par prestation</span>
                <span>{averagePricePerService.toFixed(2)} €</span>
              </div>
            )}
            <div className={styles.totalRow}>
              <span>Total</span>
              <span className={styles.totalAmount}>
                {appointment.total_amount != null
                  ? `${Number(appointment.total_amount).toFixed(2)} €`
                  : "-"}
              </span>
            </div>
          </div>
        </div>

        <div className={styles.modalFooter}>
          <Button
            color="var(--secondary-color)"
            onClick={onClose}
            style={{ padding: "7px 15px", fontSize: "0.85rem" }}
          >
            Fermer
          </Button>
        </div>
      </div>
    </div>
  );
}

export default AppointmentDetailModal;
